import { useNavigate } from '@tanstack/react-router'
import {
  Building2,
  Download,
  KeyRound,
  LogOut,
  PanelLeft,
  UserRound,
  X,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ThemeToggle,
  cn,
} from '@aeris/ui'

import { useAuth } from '../../lib/auth-context.ts'
import {
  useDesktopSelector,
  windowLabel,
  type DesktopStore,
} from '../../lib/desktop/window-store.ts'
import { setPcMode } from '../../lib/desktop.ts'
import { MODULES } from '../../lib/modules.tsx'
import { moduleRoute, moduleTitle } from '../../lib/workspace.ts'
import { ApiStatus } from '../api-status.tsx'
import { BrandLogo } from '../brand-logo.tsx'
import { ContextSwitcher } from '../context-switcher.tsx'
import { ROLE_LABELS } from '../role-labels.ts'

/**
 * A barra de tarefas do Modo PC — F138.
 *
 * Um botão por janela aberta, na ordem em que abriram: clicar traz a janela;
 * clicar na que já está na frente a minimiza — o gesto que qualquer pessoa
 * que usa Windows já tem na mão. A janela da frente é marcada duas vezes, com
 * fundo e com um traço embaixo, porque cor sozinha some num monitor de balcão
 * visto de lado (a mesma regra da barra lateral).
 *
 * À direita, o que no Modo Site mora no topo: a loja, o status da conexão, o
 * tema, a hora e a conta.
 */

const Clock = (): ReactNode => {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    let timer = 0
    // Acorda na virada do minuto, e não a cada segundo: um relógio de
    // `HH:mm` não justifica uma renderização por segundo o dia inteiro.
    const schedule = (): void => {
      const current = new Date()
      const untilNextMinute =
        60_000 - (current.getSeconds() * 1000 + current.getMilliseconds())
      timer = window.setTimeout(() => {
        setNow(new Date())
        schedule()
      }, untilNextMinute)
    }
    schedule()
    return () => {
      window.clearTimeout(timer)
    }
  }, [])

  return (
    <time
      dateTime={now.toISOString()}
      className="shrink-0 px-2 text-(length:--text-xs) tabular-nums text-(--taskbar-text)"
    >
      {now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
    </time>
  )
}

const chromeButton = cn(
  'flex h-8 items-center gap-2 rounded-(--radius-sm) px-2.5',
  'text-(length:--text-sm) text-(--taskbar-text)',
  'transition-colors duration-(--duration-fast)',
  'hover:bg-(--taskbar-item-hover) hover:text-(--taskbar-text-active)',
  'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white'
)

export const Taskbar = ({
  store,
  onSearch,
  onBeforeSwitch,
}: {
  store: DesktopStore
  onSearch: () => void
  /** Pergunta antes de trocar de loja — há ficha aberta numa janela. */
  onBeforeSwitch?: (() => Promise<boolean>) | undefined
}): ReactNode => {
  const windows = useDesktopSelector(store, (state) => state.windows)
  const activeId = useDesktopSelector(store, (state) => state.activeId)
  const notice = useDesktopSelector(store, (state) => state.notice)
  const { session, signOut, showsPage, isPlatformAdmin } = useAuth()
  const navigate = useNavigate()

  return (
    <nav
      aria-label="Barra de tarefas"
      className="flex h-10 shrink-0 items-center gap-1 border-t border-(--taskbar-border) bg-(--taskbar-bg) px-1.5"
    >
      <button
        type="button"
        onClick={onSearch}
        aria-label="Ir para uma tela (Ctrl K)"
        className={cn(chromeButton, 'shrink-0 font-(--weight-medium)')}
      >
        <BrandLogo variant="mark" className="h-5 w-5 rounded-(--radius-sm)" />
        <span>Telas</span>
        <kbd className="rounded-(--radius-sm) border border-(--taskbar-border) px-1 font-(--font-mono) text-(length:--text-xs) opacity-70">
          Ctrl K
        </kbd>
      </button>

      <div aria-hidden="true" className="mx-1 h-5 w-px bg-(--taskbar-border)" />

      <ul
        aria-label="Janelas abertas"
        className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
      >
        {windows.map((entry) => {
          const isActive = entry.id === activeId
          const label = windowLabel(entry)
          const Icon = MODULES.find(
            (item) => item.module === entry.module
          )?.Icon

          return (
            <li key={entry.id} className="min-w-0">
              <button
                type="button"
                aria-pressed={isActive}
                title={
                  entry.mode === 'minimized' ? `${label} (minimizada)` : label
                }
                onClick={() => {
                  store.dispatch(
                    isActive
                      ? { type: 'minimize', id: entry.id }
                      : { type: 'focus', id: entry.id }
                  )
                }}
                className={cn(
                  chromeButton,
                  'w-44 min-w-0',
                  isActive &&
                    'bg-(--taskbar-item-active) font-(--weight-medium) text-(--taskbar-text-active) shadow-[inset_0_-2px_0_0_currentColor]',
                  entry.mode === 'minimized' && 'opacity-70'
                )}
              >
                {Icon === undefined ? null : (
                  <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                )}
                <span className="truncate">{label}</span>
              </button>
            </li>
          )
        })}
      </ul>

      {notice === null ? null : (
        <p
          role="status"
          className="flex shrink-0 items-center gap-1.5 rounded-(--radius-sm) bg-(--warning-subtle) px-2 py-1 text-(length:--text-xs) text-(--warning)"
        >
          {notice}
          <button
            type="button"
            aria-label="Fechar o aviso"
            onClick={() => {
              store.dispatch({ type: 'dismissNotice' })
            }}
            className="rounded-(--radius-sm) p-0.5 hover:bg-(--surface-hover)"
          >
            <X aria-hidden="true" className="h-3 w-3" />
          </button>
        </p>
      )}

      <div className="flex shrink-0 items-center gap-1.5">
        <ContextSwitcher onBeforeSwitch={onBeforeSwitch} />
        <ApiStatus />
        <ThemeToggle className="text-(--taskbar-text) hover:bg-(--taskbar-item-hover) hover:text-(--taskbar-text-active)" />
        <Clock />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="account-menu"
              aria-label={session?.user.displayName ?? 'Conta'}
              className={chromeButton}
            >
              <UserRound aria-hidden="true" className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-64">
            <div className="flex flex-col gap-0.5 px-2 py-1.5">
              <span className="truncate text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                {session?.user.displayName ?? ''}
              </span>
              <span className="truncate text-(length:--text-xs) text-(--text-muted)">
                {session?.user.email ?? ''}
              </span>
              {session?.context === null ||
              session?.context === undefined ? null : (
                <span className="mt-1 text-(length:--text-xs) text-(--text-secondary)">
                  {session.context.tenantName} ·{' '}
                  {ROLE_LABELS[session.context.role]}
                </span>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                void navigate({ to: '/trocar-senha' })
              }}
            >
              <KeyRound aria-hidden="true" className="h-4 w-4" />
              Trocar senha
            </DropdownMenuItem>
            {isPlatformAdmin ? (
              <DropdownMenuItem
                onSelect={() => {
                  store.dispatch({
                    type: 'open',
                    request: {
                      module: null,
                      href: '/plataforma',
                      title: 'Contas da plataforma',
                    },
                  })
                }}
              >
                <Building2 aria-hidden="true" className="h-4 w-4" />
                Contas da plataforma
              </DropdownMenuItem>
            ) : null}
            {showsPage('baixar') ? (
              <DropdownMenuItem
                onSelect={() => {
                  store.dispatch({
                    type: 'open',
                    request: {
                      module: 'baixar',
                      href: moduleRoute('baixar'),
                      title: moduleTitle('baixar'),
                    },
                  })
                }}
              >
                <Download aria-hidden="true" className="h-4 w-4" />
                Aeris no computador
              </DropdownMenuItem>
            ) : null}
            {/*
             * Voltar para a barra lateral. Fica guardado neste computador, e o
             * menu da conta do Modo Site tem o caminho de volta.
             */}
            <DropdownMenuItem
              onSelect={() => {
                setPcMode(false)
              }}
            >
              <PanelLeft aria-hidden="true" className="h-4 w-4" />
              Voltar ao Modo Site
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                void signOut()
              }}
            >
              <LogOut aria-hidden="true" className="h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  )
}
